/**
 * NoJo Enterprise — corporate-site What's New & Roadmap source.
 *
 * Public surface only. Every entry MUST comply with
 * `/app/memory/CHANGELOG_RULES.md`:
 *   - User-visible outcome first, marketing voice, neutral.
 *   - Bilingual { en, ja }.
 *   - No paths, no endpoint names, no tooling, no internal symbols,
 *     no tech-stack identifiers, no security mechanism detail,
 *     no roadmap leakage.
 *   - Treat every entry as if a malicious researcher were reading it.
 *
 * Adapter-output shape so items merge cleanly with mapped JEC / JTR
 * items inside `useEcosystemDataset`. Both title and body are
 * bilingual { en, ja }.
 *
 * Status values (roadmap only): completed · in_testing · in_progress
 *                               · planned · launch
 *
 * To add a new entry:
 *   1. Bump the `version` (semver: MAJOR.MINOR.PATCH).
 *   2. Set `date` and `sortDate` to the JST publish date.
 *      IMPORTANT — sortDate must be the ACTUAL current JST wall-clock at
 *      the moment the change ships. Do NOT guess a plausible-looking
 *      time. To get the real value, run:
 *         TZ=Asia/Tokyo date '+%Y-%m-%dT%H:%M:%S+09:00'
 *      Setting sortDate in the future (or a fake "round" time like
 *      10:30 / 12:00 / 13:00) makes the entry appear as if it shipped
 *      hours later than now, which confuses visitors and damages trust.
 *   3. Provide bilingual title + desc — customer-outcome wording.
 *   4. Keep `_serviceId: "nojo-enterprise"` and the navy `_serviceColorHex`.
 *   5. Run through CHANGELOG_RULES.md checklist before publishing.
 */

const SERVICE_ID = "nojo-enterprise";
const SERVICE_HEX = "#0F1D2D"; // NoJo navy

// ─── Roadmap milestones ────────────────────────────────────────────
// Public, customer-outcome items only. No internal tooling, paths,
// or implementation detail.
export const NOJO_ROADMAP_ITEMS = [
  // ─── June 2026 milestone + next-gen platform coordination ───────
  {
    id: "nojo-roadmap-2026-q2-18k-milestone",
    reviewed: null,
    quarter: "q2_2026",
    status: "completed",
    category: SERVICE_ID,
    title: {
      en: "18,000+ customers reached across Japan (June 2026 milestone)",
      ja: "2026年6月、日本全国で18,000名超のお客様に到達",
    },
    desc: {
      en: "In June 2026, the nationwide English-language ETC service surpassed 18,000 customers — a major milestone thirteen years in the making. A small team has held the operating discipline behind every one of those customers, and the corporate copy across About, Leadership, HomePage, founder messages, team pages, and SEO metadata was refreshed to celebrate the moment.",
      ja: "2026年6月、英語対応の全国規模ETCサービスが18,000名を超えるお客様に到達しました。創業から13年、小さなチームで一人ひとりのお客様に向き合ってきた運用体制が支える大きな節目です。この機会に、About、Leadership、HomePage、代表メッセージ、チームページ、SEOメタデータのコーポレート表記を刷新しました。",
    },
    impact: {
      en: "Company-wide storytelling milestone",
      ja: "全社レベルの節目",
    },
    eta: null,
    progress: null,
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
    _sortDate: "2026-06-21T09:00:00+09:00",
  },
  {
    id: "nojo-roadmap-2026-q3-next-gen-jec-coordination",
    reviewed: null,
    quarter: "q3_2026",
    status: "in_progress",
    category: SERVICE_ID,
    title: {
      en: "Coordinate the next-generation JapanETCcard platform",
      ja: "次世代JapanETCcardプラットフォームの連携を主導",
    },
    desc: {
      en: "Active development is underway on the redesigned JapanETCcard.com. NoJo Enterprise's role is to keep the federation contract intact — testimonials mirror, changelog and roadmap sync, and the webhook pipeline — so the next-generation JapanETCcard experience ships without disrupting cross-site continuity for existing customers.",
      ja: "リニューアル版JapanETCcard.comの開発が現在進行中です。NoJo Enterpriseは、推薦文ミラー、体組・体制の変更履歴・ロードマップの同期、Webhookパイプラインといったフェデレーション契約を維持し、既存のお客様にとって次世代JapanETCcardの体験が継ぎ目なく提供されるよう連携を主導しています。",
    },
    impact: {
      en: "Multi-quarter platform coordination",
      ja: "複数四半期にわたるプラットフォーム連携",
    },
    eta: { en: "2026", ja: "2026年" },
    progress: null,
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
    _sortDate: "2026-07-01T09:00:00+09:00",
  },
  {
    id: "nojo-roadmap-2026-q4-jtr-next-gen-coordination",
    reviewed: null,
    quarter: "q4_2026",
    status: "in_progress",
    category: SERVICE_ID,
    title: {
      en: "Coordinate the enhanced JapanTollReceipts platform (late-2026 launch)",
      ja: "JapanTollReceipts新プラットフォームの連携を主導(2026年後半公開予定)",
    },
    desc: {
      en: "An enhanced JapanTollReceipts platform is on track for a late-2026 public launch. NoJo Enterprise is coordinating the federation bridge (bulk snapshot events, testimonials forwarding, changelog + roadmap parity) so JEC and JTR customers experience a single, unified corporate brand across the entire ecosystem when the new platform goes live.",
      ja: "JapanTollReceiptsの新プラットフォームが2026年後半の一般公開に向けて進行中です。NoJo Enterpriseは、バルクスナップショットイベント、推薦文の転送、変更履歴・ロードマップのパリティといったフェデレーションブリッジを整え、新プラットフォーム公開時にJEC・JTRのお客様がエコシステム全体で統一されたコーポレートブランド体験を得られるよう連携を主導しています。",
    },
    impact: {
      en: "Federation parity across JEC · NoJo · JTR",
      ja: "JEC · NoJo · JTRのフェデレーションパリティ",
    },
    eta: { en: "Late 2026", ja: "2026年後半" },
    progress: null,
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
    _sortDate: "2026-11-15T09:00:00+09:00",
  },
  {
    id: "nojo-roadmap-2026-q2-nav-cleanup",
    reviewed: null,
    quarter: "q2_2026",
    status: "completed",
    category: SERVICE_ID,
    title: {
      en: "Cleaner mobile navigation",
      ja: "モバイルナビゲーションの整理",
    },
    desc: {
      en: "The mobile menu was reorganized so leadership, team, founder messages, and careers all live together under Company — making the navigation shorter and easier to browse on a phone.",
      ja: "モバイルメニューを整理し、経営、チーム、代表メッセージ、採用情報を「コーポレート」セクション配下にまとめました。メニューが短くなり、スマートフォンでの閲覧がしやすくなりました。",
    },
    impact: null,
    eta: null,
    progress: null,
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
    _sortDate: "2026-05-20T10:30:00+09:00",
  },
  {
    id: "nojo-roadmap-2026-q2-unified-updates",
    reviewed: null,
    quarter: "q2_2026",
    status: "completed",
    category: SERVICE_ID,
    title: {
      en: "One place to follow every NoJo update",
      ja: "NoJoの更新情報を一か所で確認",
    },
    desc: {
      en: "What's New and the public Roadmap now include NoJo Enterprise alongside JapanETCcard and JapanTollReceipts — with a service filter to focus on any one of them or see all together.",
      ja: "「最新情報」と公開ロードマップに、NoJo EnterpriseをJapanETCcard、JapanTollReceiptsと並べて表示するようにしました。サービスフィルターで個別に絞り込んだり、まとめて確認したりできます。",
    },
    impact: null,
    eta: null,
    progress: null,
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
    _sortDate: "2026-05-20T12:00:00+09:00",
  },
  {
    id: "nojo-roadmap-2026-q2-brand-colors",
    reviewed: null,
    quarter: "q2_2026",
    status: "completed",
    category: SERVICE_ID,
    title: {
      en: "Brand colors brought through to every call-to-action",
      ja: "ブランドカラーをすべての主要ボタンに反映",
    },
    desc: {
      en: "Primary buttons across the site now consistently use NoJo's blue, green, and purple, giving the experience a more colorful, on-brand feel — especially on mobile.",
      ja: "サイト全体の主要ボタンを、NoJoのブルー、グリーン、パープルで統一しました。特にモバイルで、より色彩豊かでブランドらしい体験になっています。",
    },
    impact: null,
    eta: null,
    progress: null,
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
    _sortDate: "2026-05-20T13:00:00+09:00",
  },
  // ─── JapanChangeMoney roadmap (NoJo-managed until JCM has its own
  //     federation endpoint). Surfaced under the JapanChangeMoney
  //     service filter chip on the consolidated roadmap. ────────────
  {
    id: "japanchangemoney-roadmap-2027-q1-rebuild",
    reviewed: null,
    quarter: "q1_2027",
    status: "in_progress",
    category: "japanchangemoney",
    title: {
      en: "JapanChangeMoney — complete rebuild from scratch",
      ja: "JapanChangeMoney — サイト全面リニューアル",
    },
    desc: {
      en: "The current JapanChangeMoney beta site is being completely redesigned and rebuilt from the ground up — every interface, every page. The next-generation site will lead with a clearer comparison-aggregator focus, showing live exchange rates from every money exchange across Japan in one place.",
      ja: "現在のJapanChangeMoneyのベータ版サイトを、全画面・全ページにわたり一から完全に再設計・再構築します。次世代サイトでは、日本全国の両替所のレートを一か所で比較できる「比較アグリゲーター」機能をより分かりやすく前面に押し出します。",
    },
    impact: null,
    eta: null,
    progress: null,
    _serviceId: "japanchangemoney",
    _serviceColorHex: "#0E5FB0",
    _sortDate: "2026-07-01T00:00:00+09:00",
  },
  {
    id: "japanchangemoney-roadmap-2027-q1-launch",
    reviewed: null,
    quarter: "q1_2027",
    status: "planned",
    category: "japanchangemoney",
    title: {
      en: "JapanChangeMoney — 2027 launch",
      ja: "JapanChangeMoney — 2027年公開",
    },
    desc: {
      en: "Public launch of the redesigned JapanChangeMoney service, targeting 2027. The launch service will offer USD ⇄ JPY exchange with NoJo's own desk, alongside a live, all-Japan exchange-rate comparison chart covering money exchanges across the country.",
      ja: "リニューアル版JapanChangeMoneyの一般公開を2027年に予定しています。NoJoの自社窓口では米ドル⇄日本円の両替を提供しつつ、日本全国の両替所のレートをリアルタイムで一覧・比較できる機能を同時に公開します。",
    },
    impact: null,
    eta: { en: "2027", ja: "2027年" },
    progress: null,
    _serviceId: "japanchangemoney",
    _serviceColorHex: "#0E5FB0",
    _sortDate: "2027-01-01T00:00:00+09:00",
  },
];

// ─── Changelog (What's New) entries ────────────────────────────────
// Newest-first ordering is enforced after merging in the hook.
export const NOJO_CHANGELOG_ITEMS = [
  {
    version: "v3.21.0",
    type: "Patch",
    date: "August 7, 2026 (JST)",
    sortDate: "2026-08-07T09:11:00+09:00",
    latest: true,
    title: {
      en: "A smoother Japanese mobile display, and steadier JapanTollReceipts reviews",
      ja: "日本語表示の改善と、JapanTollReceiptsのレビュー表示を安定化",
    },
    desc: {
      en: "A few refinements landed today: a mobile display issue on the Japanese homepage is fixed, Japanese-language customer reviews for JapanTollReceipts now load more reliably, and the Customer Testimonials page gained a small 'last verified' indicator.",
      ja: "本日、いくつかの改善をリリースしました。日本語版ホームページのモバイル表示の不具合を修正し、JapanTollReceiptsの日本語レビュー表示をより安定化。さらに「お客様の声」ページに小さな「最終確認」表示を追加しました。",
    },
    changes: [],
    features: [],
    improvements: [
      {
        title: {
          en: "Mobile display fix for Japanese product names on the homepage",
          ja: "ホームページの日本語表示を一部改善（商品名の見切れを解消）",
        },
        desc: {
          en: "On some phone screens, part of a product name could extend slightly past the edge of the screen when viewing the site in Japanese. That's now fixed — the homepage displays cleanly at every screen size in both languages.",
          ja: "一部のスマートフォン画面で、日本語表示時に商品名の一部が画面端からわずかにはみ出す場合がありました。この点を修正し、日英どちらの言語でも、あらゆる画面サイズできれいに表示されるようになりました。",
        },
      },
      {
        title: {
          en: "More reliable Japanese-language reviews for JapanTollReceipts",
          ja: "JapanTollReceiptsの日本語レビュー表示をより安定化",
        },
        desc: {
          en: "Japanese customer reviews for JapanTollReceipts now load more quickly and consistently. A quality safeguard also ensures every review shown on a Japanese page is a genuine Japanese-language review, not an English one shown by mistake.",
          ja: "JapanTollReceiptsの日本語のお客様レビューが、より速く安定して表示されるようになりました。また、日本語ページには必ず正真正銘の日本語レビューのみが表示されるよう、品質チェックの仕組みも追加しています。",
        },
      },
      {
        title: {
          en: "\"Last verified\" indicator added to Customer Testimonials",
          ja: "「お客様の声」ページに「最終確認」表示を追加",
        },
        desc: {
          en: "The Customer Testimonials page now shows the same small 'last verified' freshness indicator already used elsewhere on the site, so visitors can see at a glance how current the reviews are.",
          ja: "「お客様の声」ページに、サイトの他の場所で既にご利用いただいている「最終確認」表示（鮮度インジケーター）を追加しました。レビューがどれくらい新しい情報かを一目で確認できます。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.20.1",
    type: "Patch",
    date: "August 6, 2026 (JST)",
    sortDate: "2026-08-06T23:29:00+09:00",
    latest: false,
    title: {
      en: "Smoother keyboard navigation, and faster-loading service pages",
      ja: "キーボード操作の改善と、サービスページの表示速度向上",
    },
    desc: {
      en: "A focused round of usability improvements landed today: keyboard navigation now works smoothly from the very first click on every page, the full-screen photo viewer on Signboards pages is easier to navigate with Tab and Escape, and service pages load a touch more efficiently.",
      ja: "本日、使いやすさに関する改善を複数リリースしました。すべてのページで最初のクリックからキーボード操作がスムーズに機能するようになり、看板掲出ページの全画面フォトビューアーもTabキーやEscapeキーで操作しやすくなりました。サービス詳細ページの表示もわずかに高速化しています。",
    },
    changes: [],
    features: [],
    improvements: [
      {
        title: {
          en: "Keyboard navigation now works from the first click on any page",
          ja: "どのページでも最初のクリックからキーボード操作が正しく機能",
        },
        desc: {
          en: "Visitors navigating with a keyboard (Tab key) previously had their focus skip past the top navigation on a fresh page load. That's fixed — focus now follows the expected order from the moment a page finishes loading.",
          ja: "キーボード（Tabキー）で操作する方の焦点が、ページ読み込み直後は上部ナビゲーションを飛び越えてしまう事象がありました。この点を修正し、ページの読み込み完了直後からフォーカスが想定通りの順序で移動するようになりました。",
        },
      },
      {
        title: {
          en: "Signboard photo viewer now supports full keyboard control",
          ja: "看板の全画面フォトビューアーがキーボード操作にフル対応",
        },
        desc: {
          en: "Opening a photo in the full-screen gallery on any Signboards page now moves focus into the viewer automatically, Tab cycles through the viewer's own controls, and closing it returns focus to the photo you started from.",
          ja: "看板掲出ページで写真を全画面表示すると、フォーカスが自動的にビューアー内に移動し、Tabキーではビューアー内のボタンのみを巡回します。閉じると、開く前に選んでいた写真へフォーカスが戻ります。",
        },
      },
      {
        title: {
          en: "Service detail pages load a touch more efficiently",
          ja: "サービス詳細ページの表示をわずかに効率化",
        },
        desc: {
          en: "Behind-the-scenes tuning reduced an unnecessary extra background data fetch on the JapanETCcard and JapanTollReceipts service pages, so the customer-review section on those pages settles in a little faster.",
          ja: "JapanETCcardおよびJapanTollReceiptsのサービス詳細ページで発生していた、不要なバックグラウンドの追加データ取得を裏側で調整しました。各ページのお客様の声セクションが、これまでよりわずかに速く表示されます。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.20.0",
    type: "Milestone Update",
    date: "June 21, 2026 (JST)",
    sortDate: "2026-06-21T14:00:00+09:00",
    latest: false,
    title: {
      en: "18,000-customer milestone reached — corporate story refreshed across every page",
      ja: "18,000名到達 — コーポレートページ全体で節目のストーリーを刷新",
    },
    desc: {
      en: "In June 2026, the nationwide English-language ETC service surpassed 18,000 customers — a major milestone thirteen years in the making. Every corporate page that told the previous 17,000-customer story was refreshed to celebrate the moment: About, Leadership, HomePage, founder messages, team pages, and SEO metadata all now anchor around the June 2026 milestone. Two new NoJo Enterprise roadmap items were added to publicly track the coordination work already underway on the next-generation JapanETCcard site and the enhanced JapanTollReceipts platform expected to launch later in 2026.",
      ja: "2026年6月、英語対応の全国規模ETCサービスが18,000名を超えるお客様に到達しました。創業から13年、大きな節目です。これまで17,000名の物語で記載していたコーポレートページ ─ About、Leadership、HomePage、代表メッセージ、チームページ、SEOメタデータのすべてを、2026年6月の節目を軸に刷新しました。さらに、次世代JapanETCcardサイトと、2026年後半に予定されているJapanTollReceipts新プラットフォームの公開に向けた連携作業を公開ロードマップで追跡するため、NoJo Enterpriseのロードマップ項目を2件新たに追加しました。",
    },
    changes: [],
    features: [
      {
        title: {
          en: "Every corporate page now tells the June 2026 milestone story",
          ja: "全コーポレートページで2026年6月の節目のストーリーを反映",
        },
        desc: {
          en: "About, Leadership, HomePage, founder messages, team pages, and SEO metadata were all updated together — so a visitor arriving from any entry point reads the same, current milestone framing: 18,000+ customers reached in June 2026, over thirteen years of steady growth, and next-generation platform work already in motion.",
          ja: "About、Leadership、HomePage、代表メッセージ、チームページ、SEOメタデータをまとめて更新しました。どの入り口からご覧いただいても、同じ最新の節目 ─ 2026年6月に到達した18,000名超のお客様、13年間積み重ねてきた成長、そして次世代プラットフォーム開発の進行状況 ─ が読める形になっています。",
        },
      },
      {
        title: {
          en: "Next-generation JapanETCcard platform coordination now on the public roadmap",
          ja: "次世代JapanETCcardプラットフォーム連携を公開ロードマップに追加",
        },
        desc: {
          en: "A new NoJo Enterprise roadmap item captures the coordination work underway to keep the federation contract (testimonials mirror, changelog / roadmap sync, webhook pipeline) intact as the redesigned JapanETCcard.com ships. Customers and partners can now follow the effort as a public milestone.",
          ja: "リニューアル版JapanETCcard.comの公開に向け、推薦文ミラー、変更履歴・ロードマップの同期、Webhookパイプラインといったフェデレーション契約を維持する連携作業を、NoJo Enterpriseのロードマップ項目として新たに追加しました。お客様・パートナー各社が、この取り組みを公開の節目として追跡できるようになります。",
        },
      },
      {
        title: {
          en: "Enhanced JapanTollReceipts platform launch (late 2026) now on the public roadmap",
          ja: "JapanTollReceipts新プラットフォーム公開(2026年後半)を公開ロードマップに追加",
        },
        desc: {
          en: "A second new roadmap item tracks the enhanced JapanTollReceipts platform expected to launch later in 2026 — including the federation bridge work (bulk snapshot events, testimonials forwarding, changelog + roadmap parity) that keeps JEC and JTR customers on a single, unified corporate brand.",
          ja: "2026年後半に予定されているJapanTollReceipts新プラットフォーム公開を、追加のロードマップ項目として掲載しました。バルクスナップショットイベント、推薦文の転送、変更履歴・ロードマップのパリティといったフェデレーションブリッジ作業も含めて追跡し、JEC・JTRのお客様がエコシステム全体で統一されたコーポレートブランド体験を得られるよう取り組みます。",
        },
      },
    ],
    improvements: [
      {
        title: {
          en: "Fallback customer-count string refreshed to 18,000+",
          ja: "顧客数フォールバック表記を「18,000+」に更新",
        },
        desc: {
          en: "The safe fallback string used when the live customer-count feed is momentarily unavailable was updated from '17,000+' to '18,000+' to match the June 2026 milestone. Visitors still see a real, current number even if the upstream signal is delayed.",
          ja: "リアルタイムの顧客数フィードが一時的に取得できない場合に表示する安全なフォールバック表記を「17,000+」から「18,000+」に更新し、2026年6月の節目に合わせました。上流のシグナルが遅延した場合でも、閲覧者には現在の数値をお届けします。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
  },
  {
    version: "v3.19.0",
    type: "Feature Release",
    date: "May 24, 2026 (JST)",
    sortDate: "2026-05-24T18:27:00+09:00",
    latest: false,
    title: {
      en: "Customer testimonials now read naturally in both Japanese and English across our sites",
      ja: "お客様の声を、日英のどちらでも自然に読めるよう全サイトで整えました",
    },
    desc: {
      en: "Every customer testimonial on the corporate site, and on our operated-service sites, now appears in both languages with each version written to feel native — not a word-for-word swap. The same curated set is shared across NoJo Enterprise, JapanETCcard, and JapanTollReceipts, so a story added on one site shows up, in your language, on the others.",
      ja: "コーポレートサイトおよび弊社運営の各サービスサイトに掲載しているお客様の声を、日本語・英語の両方で、それぞれの言語として自然に読める形に整えました。直訳ではなく、各言語に合わせて編集された文章でお届けします。キュレーション済みの同じセットをNoJo Enterprise・JapanETCcard・JapanTollReceiptsで共有しているため、いずれかのサイトに新しいお声が加わると、ご覧の言語で他のサイトにも反映されます。",
    },
    changes: [],
    features: [
      {
        title: {
          en: "One shared set of customer voices across the three sites",
          ja: "3つのサイトで、お客様の声を一つの共通セットとして共有",
        },
        desc: {
          en: "NoJo Enterprise, JapanETCcard, and JapanTollReceipts now read from the same curated list of approved customer testimonials. When a new story is added on one site, the same story is available, in your chosen language, on the other two without anyone having to retype or re-translate it.",
          ja: "NoJo Enterprise・JapanETCcard・JapanTollReceiptsの3つのサイトが、承認済みのお客様の声について同じキュレーション済みリストを参照する形になりました。いずれかのサイトにお声が追加されると、ご覧の言語のまま、他の2サイトでも同じお声をお読みいただけます。再入力や再翻訳の手作業は発生しません。",
        },
      },
    ],
    improvements: [
      {
        title: {
          en: "Each testimonial reads as if written in your language",
          ja: "それぞれのお声が、選んだ言語で読んでも自然に",
        },
        desc: {
          en: "Whichever language the site is shown in, each testimonial reads naturally end-to-end — punctuation, register, and phrasing fit the language you're reading, instead of feeling like a literal word-for-word version of the other side.",
          ja: "サイトの表示言語を切り替えても、各お客様の声を最初から最後まで自然な文章でお読みいただけます。句読点・敬体／常体・言い回しのすべてが表示言語に合わせて整えられており、もう一方の言語の直訳のような違和感がありません。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.18.0",
    type: "Patch",
    date: "May 24, 2026 (JST)",
    sortDate: "2026-05-24T18:20:00+09:00",
    latest: false,
    title: {
      en: "Clicking the active page in the nav now scrolls you back to the top",
      ja: "ナビゲーションで現在のページをクリックすると、上部までスクロールします",
    },
    desc: {
      en: "Visitors who clicked a top-nav link pointing at the page they were already on saw nothing happen. That now scrolls smoothly back to the top — matching the expected web behaviour. The same fix applies to the brand wordmark and the mobile menu.",
      ja: "現在表示中のページのリンクをナビゲーションでクリックしても何も起きなかった挙動を解消しました。クリック時にページ上部まで滑らかにスクロールするようになり、一般的なウェブの操作感と一致します。ブランドロゴおよびモバイルメニューでも同様に動作します。",
    },
    changes: [],
    features: [],
    improvements: [
      {
        title: {
          en: "Smooth scroll-to-top on same-page nav clicks",
          ja: "同じページのナビをクリックすると、トップまで滑らかにスクロール",
        },
        desc: {
          en: "If you're already on a page and you click that page in the nav, the site treats it as a refresh cue and scrolls you back to the top of the page.",
          ja: "現在表示中のページを、ナビゲーション上で再度クリックすると、サイトはそれを「再表示の合図」として受け取り、ページ上部までスクロールします。",
        },
      },
      {
        title: {
          en: "Reduced-motion users get an instant jump",
          ja: "「視差効果を減らす」設定の方は即時ジャンプ",
        },
        desc: {
          en: "Visitors who have their device's 'reduce motion' setting on get an instant jump to the top of the page rather than a smooth animation.",
          ja: "端末の「視差効果を減らす」設定が有効になっている方には、アニメーションではなく、即時ジャンプでページ上部に移動します。",
        },
      },
      {
        title: {
          en: "Open-in-new-tab still works as expected",
          ja: "「新しいタブで開く」操作はこれまで通り",
        },
        desc: {
          en: "Middle-click, ctrl-click, ⌘-click, and shift-click on nav links continue to open the destination in a new tab or window, just as they always have.",
          ja: "ナビゲーションリンクへの中クリック・Ctrl+クリック・⌘+クリック・Shift+クリックは、これまで通り新しいタブまたはウィンドウで開きます。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.17.0",
    type: "Feature Release",
    date: "May 22, 2026 (JST)",
    sortDate: "2026-05-22T05:39:00+09:00",
    latest: false,
    title: {
      en: "Founder's story expanded with archival photos and two new chapters",
      ja: "創業者のメッセージを拡充：当時の写真と2つの新章を追加",
    },
    desc: {
      en: "Founder's message pages now include archival photos from the 2013–2018 era, two new chapters (2018 office move, 2025 Okinawa expansion), and an authentic October 2013 social-media post. JapanTollReceipts was rewritten in a partnership-first voice.",
      ja: "創業者のメッセージページに、2013〜2018年の当時の写真、2つの新章（2018年オフィス移転、2025年沖縄展開）、2013年10月の実際のSNS投稿を追加。JapanTollReceiptsの章はパートナーシップ重視の語り口に書き直しました。",
    },
    changes: [],
    features: [
      {
        title: {
          en: "New \"From the Founder\" section on the homepage",
          ja: "トップページに「創業者からのメッセージ」セクションを新設",
        },
        desc: {
          en: "A dedicated homepage block now introduces Adam Jones with a portrait and short intro paragraph, and pairs it with two link cards that lead directly to the long-form founder's messages for JapanETCcard and JapanTollReceipts. Visitors no longer have to dig through the footer to find the story behind each service.",
          ja: "トップページに、アダム・ジョーンズのポートレートと短い紹介文を組み合わせた専用ブロックを設けました。同じセクション内に2枚のリンクカードを並べ、JapanETCcardとJapanTollReceiptsそれぞれの創業者メッセージ（本編）へ1クリックでアクセスできます。各サービスの背景をお読みいただくのに、フッターまでスクロールしていただく必要がなくなりました。",
        },
      },
      {
        title: {
          en: "Founder-message link cards on each service detail page",
          ja: "各サービスの詳細ページに、創業者メッセージへのリンクカードを設置",
        },
        desc: {
          en: "The JapanETCcard and JapanTollReceipts service detail pages each carry a clean link card with Adam Jones's portrait, a short teaser, and a clear path through to the full founder's message for that service. The reader stays inside the same story arc the service page already sets up.",
          ja: "JapanETCcardとJapanTollReceiptsの詳細ページに、アダム・ジョーンズのポートレート、短い導入文、そして該当サービスの創業者メッセージ全文へのリンクを組み合わせた、わかりやすいカードを追加しました。サービスページが用意している物語の流れを保ったまま、その続きへとお読みいただける導線です。",
        },
      },
      {
        title: {
          en: "Two new dated chapters in the JapanETCcard story — 2018 office move, 2025 Okinawa expansion",
          ja: "JapanETCcardの物語に、新しい2章を追加 ─ 2018年オフィス移転、2025年の沖縄展開",
        },
        desc: {
          en: "The 2018 chapter shows the move from the original small Yokosuka office across the street to a 2nd-floor space directly facing the main gate on Route 16, with three photos: a wide street view, a close-up of the JapanETCcard hanging sign, and the storefront banner. The 2025 chapter introduces the expansion into Okinawa with three new signboards along Route 58 — covering Camp Foster, Oyama, and central Okinawa — and links readers directly to the Okinawa signboard map.",
          ja: "2018年の章では、横須賀の最初の小さなオフィスから、国道16号線・メインゲート正面の2階拠点への移転を、3枚の写真でご紹介しています ─ 通りからの街並みカット、バルコニーに掲げたJapanETCcardの吊り下げ看板の寄り、そして正面の大型バナーです。2025年の章では、国道58号線に沿って3か所（キャンプ・フォスター周辺、大山、本島中部）に設置した新しい広告看板を中心に、沖縄エリアへの展開を語り、読者の方が沖縄の看板マップへ直接移動できるリンクも添えています。",
        },
      },
      {
        title: {
          en: "Authentic October 2013 social-media post preserved as a highlighted callout",
          ja: "2013年10月の実際のSNS投稿を、ハイライト化された一枚のカードとして掲載",
        },
        desc: {
          en: "An original social-media post Adam Jones wrote in October 2013 — about sleeping at the office in a sleeping bag while building the company — is now preserved in his own words, paired side-by-side with the very photo he posted alongside it. Rendered as a single highlighted purple-accented card so the moment lands with the weight it earned.",
          ja: "2013年10月、アダム・ジョーンズが「寝袋でオフィスに泊まり込みながら会社を立ち上げていた頃」について実際に投稿したSNSの文章を、原文のまま掲載しています。当時の投稿と一緒にアップされた寝袋の写真を、文章とちょうど横並びで配置し、紫のアクセントを効かせた1枚のハイライトカードとして仕上げました。創業期の重みが、しっかり伝わる構成です。",
        },
      },
    ],
    improvements: [
      {
        title: {
          en: "Archival photos placed at the moments they belong to",
          ja: "歩みの「その瞬間」に合わせて、当時の写真を配置",
        },
        desc: {
          en: "Each early-days photograph now sits in the chapter it actually belongs to — the first storefront and small-desk grind in the 2013 turning-point chapter, the early team meeting in the 50-card pilot chapter, the June 2014 whiteboard sessions in the patience-was-the-product chapter. Every image carries its own dated caption in the founder's own voice, so the reader follows the journey moment by moment rather than scanning a generic gallery.",
          ja: "創業初期の各写真を、本当にその瞬間に対応する章へと配置し直しました ─ 2013年の「転機」章に最初の店舗と小さなデスクでの作業風景を、50枚パイロット運用の章に初期チームミーティングを、「忍耐がプロダクトだった」章に2014年6月のホワイトボードセッションを。各写真には、創業者本人の語り口で年代の入った個別キャプションを添えています。「ひとまとめのギャラリー」を眺めるのではなく、ひとつの瞬間ごとに歩みを辿っていただける構成です。",
        },
      },
      {
        title: {
          en: "JapanTollReceipts founder's message rewritten with a partnership-first voice",
          ja: "JapanTollReceiptsの創業者メッセージを、パートナーシップを軸に書き直し",
        },
        desc: {
          en: "The JTR founder's message no longer reads as a critique of the existing official ETC usage record system. It now positions that system as the trusted, authoritative backbone every record is anchored to, with JTR as a modern automation and intelligence layer that complements it — scheduled report delivery, vehicle detection, smart alerts, and bilingual fleet visibility. The wording throughout the chapter is partnership-friendly and respectful, in line with how we genuinely operate alongside the official infrastructure.",
          ja: "JapanTollReceiptsの創業者メッセージから、既存の公式ETC利用照会システムを批判するような表現を排し、全面的に書き直しました。改訂版では、公式システムを「すべての記録の出どころとなる、信頼できる基盤」として明確に位置づけ、JTRはその上に重ねる現代的な自動化・インテリジェンスレイヤー ─ 定期配信、車両自動特定、スマートアラート、日英バイリンガルでの車両管理ビュー ─ として丁寧にご紹介します。章全体の語り口を、パートナーシップを前提とした、相手への敬意を保つ表現に統一しました。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.16.0",
    type: "Feature Release",
    date: "May 22, 2026 (JST)",
    sortDate: "2026-05-22T05:38:00+09:00",
    latest: false,
    title: {
      en: "Contact-form submissions now send an instant bilingual thank-you confirmation",
      ja: "お問い合わせフォーム送信直後に、日英バイリンガルの受付確認メールが届くように",
    },
    desc: {
      en: "Submitting the contact form now triggers a branded confirmation in the visitor's language (English or Japanese), with a 24-hour business-day response promise and a copy of the message. The internal notification was also polished with a cleaner letterhead layout.",
      ja: "公式のお問い合わせフォームの送信直後に、お客様の言語（日本語または英語）でブランド統一の受付確認メールが届くようになりました。営業日24時間以内のご返信目安と、お送りいただいた内容の控えを含みます。社内向け通知もレターヘッド調に整え、モバイル表示にも最適化しました。",
    },
    changes: [],
    features: [
      {
        title: {
          en: "Customer auto-acknowledgment in EN and JA",
          ja: "日英バイリンガルでの自動受付確認メール",
        },
        desc: {
          en: "Every visitor who submits the contact form now receives a branded confirmation back at the address they entered. The message language follows the page language: English from the English pages, Japanese from the Japanese pages. It restates the customer's message for their records, sets a clear 24-hour business-day response expectation, and signs off with the NoJo Enterprise letterhead. Replying to the confirmation routes the customer's follow-up straight back to the team in the same thread.",
          ja: "お問い合わせフォームを送信されたすべての方に、ご記入いただいたアドレス宛で、ブランドが整った受付確認メールが届きます。メールの言語はページの言語に合わせ、英語ページからは英語で、日本語ページからは日本語で配信されます。本文には、お送りいただいた内容の控え、営業日24時間以内のご返信目安、そしてNoJo Enterpriseのレターヘッドが含まれます。確認メールにご返信いただければ、同じスレッドのまま、担当チームへ追加のお問い合わせをお寄せいただけます。",
        },
      },
    ],
    improvements: [
      {
        title: {
          en: "Refined internal notification email — letterhead layout, mobile-friendly, instant logo",
          ja: "社内向け通知メールも整理 ─ レターヘッド調、モバイル最適化、ロゴの即時表示",
        },
        desc: {
          en: "The notification the support team receives when a visitor writes in has been polished end-to-end. The layout now reads like a quiet corporate letter — small left-aligned brand mark, serif headline, hairline divider rows, dark-navy reply button, classic letterhead footer. The receipt time is shown in a natural-language format (\"May 22, 2026, 5:25 AM JST\") rather than a raw timestamp. Side padding collapses gracefully on phones, long email addresses wrap instead of forcing horizontal scroll, and the brand logo travels with the message so it always renders the moment the email opens.",
          ja: "お客様からお問い合わせをいただいた際にサポートチームへ届く通知メールも、全体を整理しました。落ち着いたコーポレートレター調のレイアウトに統一し ─ 小さく左寄せのブランドマーク、セリフ体の見出し、細い区切り罫、ダークネイビーの返信ボタン、クラシックなレターヘッド調のフッター ─ 受信日時の表示も、機械的なタイムスタンプではなく自然な書き方（例：「2026年5月22日、午前5:25 JST」）に変えています。スマートフォンでは余白も自然に詰まり、長いメールアドレスは横スクロールせず折り返され、ブランドロゴはメッセージ本体と一緒に届くため、開いた瞬間からきれいに表示されます。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.15.0",
    type: "Patch",
    date: "May 22, 2026 (JST)",
    sortDate: "2026-05-22T05:37:00+09:00",
    latest: false,
    title: {
      en: "Every service now uses a unified \"support@\" contact address",
      ja: "各サービスのお問い合わせ先メールアドレスを「support@」に統一",
    },
    desc: {
      en: "Every published contact email across the site — for JapanETCcard, JapanTollReceipts, JapanChangeMoney, and the corporate desk — now follows the same simple pattern: support@[service-domain]. JapanChangeMoney was the one still using a different prefix.",
      ja: "サイト全体のお問い合わせ用メールアドレスを「support@（サービスのドメイン）」の表記に統一しました ─ JapanETCcard、JapanTollReceipts、JapanChangeMoney、そして NoJo Enterprise 本体まで含めて共通の書き方です。JapanChangeMoneyのみ別の表記でしたが、これで揃いました。",
    },
    changes: [],
    features: [],
    improvements: [
      {
        title: {
          en: "Consistent contact-address naming across all services",
          ja: "すべてのサービスで統一された、わかりやすい連絡先表記",
        },
        desc: {
          en: "Every service detail page now shows a \"support@\" contact email matching the service's own domain. The pattern is the same regardless of which service the visitor is reading about, so customers no longer need to remember which service uses which prefix.",
          ja: "サービス詳細ページに掲載するお問い合わせ用メールアドレスを、それぞれのサービスのドメインに揃えた「support@」表記に統一しました。どのサービスのページをご覧いただいても同じ書き方になっているため、サービスごとに異なる表記を覚えておく必要がなくなっています。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.14.0",
    type: "Feature Release",
    date: "May 21, 2026 (JST)",
    sortDate: "2026-05-21T08:25:00+09:00",
    latest: false,
    title: {
      en: "Leadership Team and Our AI Team are now one click away from the top navigation",
      ja: "リーダーシップとAIチームに、上部メニューから1クリックでアクセスできるように",
    },
    desc: {
      en: "Three small refinements landed today. The About Us dropdown in the top navigation now lists four pages in this order — About Us, Leadership Team, Our AI Team, and Signboards — so visitors can reach the people behind NoJo without scrolling to the footer or digging through a side menu. The Services page \"Our AI Team\" banner became a single large click target, so tapping anywhere on the purple band routes to the AI Team page. And several colored sections were retuned for easier reading — eyebrow labels switched to amber on deep-blue backgrounds, dim body paragraphs were lightened, and a previously-invisible accent stripe on the About CTA was made visible.",
      ja: "本日、3点の調整を反映しました。上部ナビゲーションの「会社情報」ドロップダウンに4項目を順番通り（会社情報、リーダーシップ、AIチーム、看板掲出）に並べました。NoJoの背後にいるメンバーのページに、フッターまで移動したりサイドメニューを開いたりせず、1クリックでアクセスできます。また、サービスページの「私たちのAIチーム」紫色のバナー全体がクリック可能になり、バナーのどの部分をタップしてもAIチームのページに移動できます。さらに、暗色背景に重ねていた色付きラベルや本文の読みやすさを見直し、文字色の調整を行いました。",
    },
    changes: [],
    features: [
      {
        title: {
          en: "About Us top-nav dropdown now lists four pages",
          ja: "上部メニューの「会社情報」に4ページを集約",
        },
        desc: {
          en: "Hovering or focusing on About Us in the top navigation now reveals four child links in this order: About Us, Leadership Team, Our AI Team, and Signboards. The Leadership and AI Team pages were previously only reachable from the homepage or the footer, which made them hard to find. They are now permanently a single click away from anywhere on the site.",
          ja: "上部ナビゲーションの「会社情報」にカーソルを合わせる、またはフォーカスすると、4つの子ページ（会社情報、リーダーシップ、AIチーム、看板掲出）がこの順に表示されるようになりました。これまで「リーダーシップ」と「AIチーム」のページは、トップページやフッターからしか到達できず、お探しいただくのが難しい状態でした。本日より、サイトのどこからでも1クリックでアクセスできます。",
        },
      },
    ],
    improvements: [
      {
        title: {
          en: "Services AI Team banner is now fully clickable",
          ja: "サービスページの「AIチーム」バナー全体がクリック可能に",
        },
        desc: {
          en: "On the Services page, the purple \"Our AI Team\" banner — the eyebrow label, the headline, the row of five member portraits, and the white \"Meet the team\" button on the right — is now a single large clickable surface. Tapping any part of the band routes to the AI Team page. The white pill stays as a visual affordance and the banner now lifts and casts a soft brand-purple shadow on hover.",
          ja: "サービスページの「私たちのAIチーム」紫色のバナー（小さな見出し、ヘッドライン、5人のメンバーの円形ポートレート、右端の白い「AIチームを見る」ボタン）の全体が、ひとつの大きなクリック可能エリアになりました。バナーのどこをタップしても、AIチームのページに移動します。白いボタンは視覚的な目印として残り、ホバー時にはバナー全体が浮き上がり、ブランドの紫色の柔らかな影が落ちる動きが加わります。",
        },
      },
      {
        title: {
          en: "Readability tuning across the colored sections",
          ja: "色付きセクションの可読性を調整",
        },
        desc: {
          en: "On the deep-blue \"service snapshot\" band, the deep-blue AI Operations strip, and the About Us call-to-action band, small eyebrow labels that previously sat color-on-color in two close shades of blue have been switched to amber, which is much easier to read on a blue background. Body paragraph text in several dim sections was also lightened from 55-70 percent white to 80-85 percent white. A previously-invisible accent stripe on the About Us CTA band was switched to amber so it visibly punctuates the section.",
          ja: "ディープブルーの「サービススナップショット」帯、ディープブルーの「AI支援オペレーション」帯、「会社情報」ページのCTA帯では、これまで青みの近い2色が重なって読みづらかった小さな見出しラベルを、青背景でも読みやすいアンバー（琥珀色）に変更しました。また、暗いセクションの本文の文字色を、白の55〜70%の濃度から80〜85%へ引き上げ、読みやすくしています。さらに、「会社情報」ページのCTA帯にあった、ほぼ見えなかったアクセントストライプもアンバーに切り替え、視覚的なリズムがしっかり伝わるようになりました。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.13.0",
    type: "Patch",
    date: "May 21, 2026 (JST)",
    sortDate: "2026-05-21T08:10:00+09:00",
    latest: false,
    title: {
      en: "AI Team page redesigned to feel lighter and more professional, and hero photos no longer carry a color tint",
      ja: "AIチームページを軽やかでプロフェッショナルなデザインに刷新、ヒーロー写真の色被りを解消",
    },
    desc: {
      en: "Two design refinements landed today. The AI Team page (Our AI Team) was rebuilt with a predominantly white, professional layout — clean white sections with light gray dividers, white profile cards with thin colored role-stripes, and a single brand-purple closing band at the bottom for a brand flourish. Across the homepage, hero and feature photos no longer carry a purple or blue color cast: a small, neutral, near-transparent darkening sits only where headline text overlaps the image, so the photos themselves read clear and unfiltered.",
      ja: "本日、デザインの2つの調整を反映しました。AIチームページ（私たちのAIチーム）を、白を基調としたよりプロフェッショナルなレイアウトに刷新しました。クリーンな白のセクションとライトグレーの区切り線、ロールカラーの細いストライプをあしらった白のプロフィールカード、そして最後にブランドカラーの紫色のCTA帯を1つだけ配置しています。トップページでは、ヒーロー写真や紹介写真に紫色や青色の色被りがかかる仕様をやめ、見出しの文字が重なる部分にのみ、ごく薄いニュートラルな暗色グラデーションを残しました。写真そのものはクリアに、フィルター無しで表示されます。",
    },
    changes: [],
    features: [],
    improvements: [
      {
        title: {
          en: "AI Team page rebuilt with a white, professional layout",
          ja: "AIチームページを白基調のプロフェッショナルなレイアウトに刷新",
        },
        desc: {
          en: "The Our AI Team page was previously dominated by deep purple and blue panels. It has been rebuilt around white and light gray sections with subtle borders, white profile cards with role-colored top stripes (director, QA lead, analyst), and a single brand-purple band at the bottom to close the page. The result feels lighter, calmer, and more in line with the rest of the corporate site.",
          ja: "「私たちのAIチーム」ページは、これまでディープパープルとブルーの大きなパネルが画面の多くを占めていました。本日より、白とライトグレーのセクション、控えめなボーダー、ロールカラー（ディレクター・QAリード・アナリスト）のトップストライプを添えた白いプロフィールカードを基本構成とし、ページ最下部にのみブランドの紫色のCTA帯を1つ配置する構成に刷新しました。全体に軽やかで落ち着いた印象になり、コーポレートサイト全体のトーンとも揃いました。",
        },
      },
      {
        title: {
          en: "Hero and feature photos no longer carry a color tint",
          ja: "ヒーロー写真や紹介写真の色被りを解消",
        },
        desc: {
          en: "Photographs across the homepage hero, the team strip, the three focus cards, the commitment band, and the Services AI Team card previously had a purple or blue gradient laid over them, which gave the imagery a colored cast. Those overlays were removed; in their place is a very subtle, near-transparent neutral darkening that only appears at the edge where headline text overlaps the photo. The images themselves now read clear and uncolored.",
          ja: "これまでトップページのヒーロー、AIチーム紹介写真、3枚のフォーカスカード、コミットメント帯、サービスページのAIチームカードでは、画像の上に紫色や青色のグラデーションがかかっており、写真全体に色がかぶって見えていました。これらの色付きオーバーレイを廃止し、見出しの文字が画像にかかる端の部分にのみ、ごく薄いニュートラル（無彩色）の暗色グラデーションを残す形に変更しました。写真そのものは色被りなく、本来の見え方で表示されます。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.12.0",
    type: "Patch",
    date: "May 21, 2026 (JST)",
    sortDate: "2026-05-21T07:55:00+09:00",
    latest: false,
    title: {
      en: "A more colorful site, and the latest version pills stay readable on mobile",
      ja: "サイトがより色鮮やかに、最新バージョン表示がモバイルで途切れないように",
    },
    desc: {
      en: "Two visible refinements landed today. Several large dark sections across the homepage, About, Our Team and Services pages have been recolored into the NoJo brand palette — deep purple and brand blue — so the site reads as unmistakably ours from top to bottom, never as a flat black canvas. On What's New, the row of latest service version pills (NoJo · JapanETCcard · JapanTollReceipts) now reflows to a second line on narrow phone screens, so the JapanTollReceipts version number is never clipped at the edge of the screen anymore.",
      ja: "本日、目に見える2点の調整を反映しました。トップページ、私たちについて、AIチーム、サービスの各ページにあった大きな暗色のセクションを、NoJoのブランドカラーであるディープパープルとブランドブルーに塗り直しました。サイト全体がより一貫してNoJoらしく見え、黒一色の印象を残さないようになっています。また「最新情報」では、各サービスの最新バージョン表示（NoJo・JapanETCcard・JapanTollReceipts）が、狭いスマートフォン画面では2行に折り返して表示されるようになり、JapanTollReceiptsのバージョン番号が画面端で途切れることがなくなりました。",
    },
    changes: [],
    features: [],
    improvements: [
      {
        title: {
          en: "Brand color refresh across major page sections",
          ja: "主要ページのブランドカラー刷新",
        },
        desc: {
          en: "Large dark panels on the homepage service snapshot, the About AI engine and CTA bands, the Our Team operating-model and analyst bands, and one feature card on Services were swapped from a dark navy to brand deep purple and brand blue. Section rhythm now alternates purple and blue so each page reads as colorful and intentional, not as a black backdrop. The footer was left as is.",
          ja: "トップページのサービス概況、私たちについての「AIエンジン」セクションとCTA帯、AIチームの「運営モデル」とアナリスト紹介帯、サービスページの1枚のフィーチャーカードを、ダークネイビーからブランドのディープパープルとブランドブルーに切り替えました。各セクションが紫と青で交互に展開し、ページ全体が黒一色ではなく、色を意図したリズムで読めるようになっています。フッターはそのままの仕上げを維持しています。",
        },
      },
      {
        title: {
          en: "Latest version pills now wrap instead of being cut off on mobile",
          ja: "最新バージョン表示がモバイルで折り返し、途切れない表示に",
        },
        desc: {
          en: "The hero of What's New shows the most recent shipped version for each operated service side by side. On narrow phone widths the row used to scroll horizontally and the right-most pill (JapanTollReceipts) could appear truncated. The row now wraps to a second line so every version number is fully visible without horizontal scrolling.",
          ja: "「最新情報」ヘッダーでは、運営中の各サービスの最新リリースバージョンを横並びで表示しています。これまでは狭いスマートフォン幅で右端のバージョン（JapanTollReceipts）が見切れて表示されることがありました。今回からは行が自動的に2段目に折り返されるようになり、横スクロールなしで全てのバージョン番号を確認できます。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.11.0",
    type: "Feature Release",
    date: "May 21, 2026 (JST)",
    sortDate: "2026-05-21T04:46:00+09:00",
    latest: false,
    title: {
      en: "Customer testimonials now appear in Japanese the moment they're published",
      ja: "お客様の声が公開と同時に日本語で表示されるようになりました",
    },
    desc: {
      en: "Customer testimonials arrive from JapanETCcard (and soon JapanTollReceipts) in English only. Starting today, every new testimonial is automatically translated into professional Japanese in the background — so visitors reading the site in Japanese see the JA version instantly, never a delay.",
      ja: "お客様の声は、JapanETCcard（および今後JapanTollReceipts）から英語のみで配信されます。本日より、新しいお客様の声が公開されると、自動的にバックグラウンドで丁寧な日本語に翻訳されるようになりました。日本語でサイトをご覧の方は、待ち時間なくJapaneseバージョンをご覧いただけます。",
    },
    changes: [],
    features: [
      {
        title: {
          en: "Automatic background translation for every new testimonial",
          ja: "新しいお客様の声を自動でバックグラウンド翻訳",
        },
        desc: {
          en: "A new background process checks for newly-published testimonials on a regular cadence and translates them into Japanese ahead of time, using a professional translation model. Translations are cached, so each testimonial is translated exactly once. Visitors reading the site in Japanese now see the JA version on the very first load.",
          ja: "新たに導入したバックグラウンド処理が、定期的に新しいお客様の声を検出し、専門の翻訳モデルを使用して事前に日本語へ翻訳します。翻訳結果はキャッシュされ、各お客様の声は一度だけ翻訳されます。日本語でサイトをご覧の方は、最初のページ読み込みから日本語版をご覧いただけます。",
        },
      },
      {
        title: {
          en: "Re-translation when source text is updated",
          ja: "原文が更新された場合の再翻訳",
        },
        desc: {
          en: "If an existing testimonial's English text is later edited upstream — for example, a typo correction — the system detects the change and re-translates only that item, keeping the Japanese version accurately aligned with the latest source.",
          ja: "公開済みのお客様の声の英文が後から修正された場合（誤字の修正など）、システムが変更を検出し、その項目のみを再翻訳します。最新の原文に沿った正確な日本語版を保ちます。",
        },
      },
    ],
    improvements: [
      {
        title: {
          en: "Same site framework now ready to translate JapanTollReceipts testimonials",
          ja: "JapanTollReceiptsのお客様の声にも対応する準備完了",
        },
        desc: {
          en: "The same translation pipeline is wired and ready to translate testimonials from JapanTollReceipts the moment its testimonials feed comes online. No additional work needed on the NoJo Enterprise site to pick them up.",
          ja: "同じ翻訳パイプラインで、JapanTollReceiptsのお客様の声フィードが公開され次第、自動的に翻訳できるよう準備が整っています。NoJo Enterpriseサイト側での追加作業は不要です。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.10.0",
    type: "Patch",
    date: "May 21, 2026 (JST)",
    sortDate: "2026-05-21T04:37:00+09:00",
    latest: false,
    title: {
      en: "Dates and times now display correctly in JST for every visitor",
      ja: "日付と時刻を、すべての閲覧者に対して正しい日本時間で表示",
    },
    desc: {
      en: "Every timestamp across the site — What's New, Roadmap, Testimonials, the live freshness badge — now shows the correct Japan Standard Time wall-clock value regardless of where the visitor is reading from. Behind-the-scenes reliability work also landed today to make the site continue to respond quickly and consistently.",
      ja: "サイト全体のすべてのタイムスタンプ（最新情報、ロードマップ、お客様の声、最終確認バッジ）が、ご利用の地域に関わらず正しい日本時間（JST）で表示されるようになりました。あわせて、サイトの応答速度と安定性を高めるための裏側の改善も本日リリースしています。",
    },
    changes: [],
    features: [],
    improvements: [
      {
        title: {
          en: "Timestamps anchored to Japan Standard Time everywhere",
          ja: "タイムスタンプをすべて日本時間（JST）基準に統一",
        },
        desc: {
          en: "Visitors reading the site from outside Japan previously saw dates and times shifted to their own timezone while still labeled JST. Every public-facing date and time on NoJo Enterprise now renders in true Japan Standard Time — same hour, same date — whether the reader is in Tokyo, New York, or anywhere in between.",
          ja: "これまで日本国外から閲覧されると、ローカルタイムゾーンに変換された日付・時刻が「JST」として表示される事象がありました。本日のリリースで、NoJo Enterpriseサイト上に表示されるすべての日付・時刻が、閲覧者の所在地に関わらず実際の日本時間（JST）で表示されるようになりました。",
        },
      },
      {
        title: {
          en: "Behind-the-scenes reliability improvements",
          ja: "裏側の安定性向上",
        },
        desc: {
          en: "Internal infrastructure cleanup landed today to keep the site loading quickly and responding consistently — no visible UI changes, but the foundation supporting NoJo Enterprise is now leaner and easier to maintain going forward.",
          ja: "本日、サイトの読み込み速度と応答の一貫性を維持するための基盤整備を実施しました。画面上の変化はありませんが、NoJo Enterpriseを支える基盤がより軽量で保守しやすい状態になっています。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.9.0",
    type: "Feature Release",
    date: "May 21, 2026 (JST)",
    sortDate: "2026-05-21T03:50:00+09:00",
    latest: false,
    title: {
      en: "Signboards everywhere, a single Okinawa pin map, and a faster, friendlier top bar",
      ja: "看板の導線を全面強化、沖縄マップを3ピン統合、ヘッダーの反応も大幅改善",
    },
    desc: {
      en: "Today's release brings the Signboards story into the spotlight — a new showcase appears on the homepage and on the JapanETCcard page, the Okinawa detail map now pins all three Route 58 boards on a single view, and the top-left brand link is now instantly clickable with clear hover feedback. The hero photo and large N mark were also tuned so the homepage feels more balanced and inviting.",
      ja: "本日のリリースでは、「看板掲出」の導線を大幅に強化しました。ホームページとJapanETCcardページに看板の専用ショーケースを新設し、沖縄の詳細マップでは国道58号線の3区画すべてを1枚のマップにピン表示。ヘッダー左上のロゴリンクも即座に反応するように改善し、ホバー時の視覚フィードバックも追加しました。あわせて、ヒーロー画像内のコピー位置と大きなNマークの収まりも調整しています。",
    },
    changes: [],
    features: [
      {
        title: {
          en: "Signboards showcase on the homepage",
          ja: "ホームページに看板ショーケースを新設",
        },
        desc: {
          en: "A new editorial band on the homepage introduces every NoJo Enterprise signboard across Japan — four photo cards, each with region tag, route, location, and a one-tap link straight to the signboard's detail page. \"Browse all signboards\" and \"Detailed map\" call-to-action buttons sit alongside the showcase.",
          ja: "ホームページに、NoJo Enterpriseの全看板をご紹介する専用バンドを新設しました。4枚のフォトカードに、地域タグ、路線、所在地を表示し、ワンタップで各看板の詳細ページへ移動できます。「すべての看板を見る」と「詳細マップ」の主要ボタンも併設しています。",
        },
      },
      {
        title: {
          en: "JapanETCcard signboards section on the JapanETCcard page",
          ja: "JapanETCcardページに看板セクションを追加",
        },
        desc: {
          en: "Visitors reading about JapanETCcard can now see the four physical signboards advertising the service across Japan, with on-site photos, region context and direct links into each signboard's detail page.",
          ja: "JapanETCcardのサービス紹介ページに、日本各地でJapanETCcardを掲出している4区画の看板セクションを追加。現地写真と地域情報を表示し、各看板の詳細ページへ直接アクセスできます。",
        },
      },
      {
        title: {
          en: "All three Okinawa signboards on one map",
          ja: "沖縄3区画を1枚のマップに統合表示",
        },
        desc: {
          en: "The Okinawa Detailed Map now shows all three Route 58 signboards (B-13, B-36A Camp Foster, Oyama) as three distinct red pins on the same map, zoomed to fit. Each pin is clickable and leads to that signboard's detail page; a clean legend lists all three locations.",
          ja: "「沖縄詳細マップ」を刷新し、国道58号線沿いの3区画（B-13、B-36A キャンプ・フォスター、大山）をすべて同じマップ上に独立した赤いピンとして表示するようにしました。3区画が1枚のマップに収まるようにズーム調整し、各ピンをクリックすると詳細ページへ。下部に3区画の位置を示す凡例も追加しています。",
        },
      },
    ],
    improvements: [
      {
        title: {
          en: "Faster, more responsive brand link in the header",
          ja: "ヘッダーのブランドリンクの応答速度を改善",
        },
        desc: {
          en: "The top-left NoJo Enterprise logo and wordmark now navigate back to the homepage instantly on the first click, with a clear hover wash, press-down feedback, and a focus ring for keyboard navigation — so visitors always know exactly what they're clicking.",
          ja: "ヘッダー左上のNoJo Enterpriseロゴ・ワードマークを、1回のクリックで即座にホームへ戻るように改善しました。ホバー時の背景ハイライト、クリック時の押し込みフィードバック、キーボード操作向けのフォーカスリングを追加し、何をクリックしているかが常に明確になりました。",
        },
      },
      {
        title: {
          en: "Detailed Maps header condensed so the map shows up first",
          ja: "詳細マップのヘッダーを圧縮し、マップを上部に",
        },
        desc: {
          en: "The Detailed Maps page header — title, region tag — is now a single tight row above the map, so the live Google Maps embed is the first thing visitors see when the page loads. The longer description was moved below the map for context.",
          ja: "「詳細マップ」ページのヘッダー（タイトル、地域タグ）を1行のコンパクトな表示に変更。ページを開いた瞬間にGoogleマップが目に入るようにしました。詳細な説明文は、マップの下に移動して補足情報として配置しています。",
        },
      },
      {
        title: {
          en: "Homepage hero N mark now sits fully within the photo",
          ja: "ホームページのN マークをヒーロー画像内にきれいに収納",
        },
        desc: {
          en: "The large N mark on the homepage was shifted down so it sits fully inside the toll-gate photo — no more clipping into the navigation area above. The mark now reads as a complete, vertical brand mark anchored within the image.",
          ja: "ホームページの大きなNマークを下方向にシフトし、横須賀の料金所写真の中にきれいに収まるように調整。上部ナビゲーションエリアへのはみ出しを解消し、画像内に完全な縦型ブランドマークとして表示されるようになりました。",
        },
      },
      {
        title: {
          en: "Homepage hero copy and buttons positioned lower on the photo",
          ja: "ホームページのヒーローコピーとボタンの位置を下げて配置",
        },
        desc: {
          en: "The headline, supporting copy, and three primary call-to-action buttons on the homepage hero (See What's New, Learn more, Who we are) were repositioned lower within the toll-gate photo, giving the navbar more breathing room and the content a more confident anchor inside the image.",
          ja: "ホームページのヒーロー部に表示される、見出し、説明文、3つの主要ボタン（最新情報を見る、サービスを見る、私たちについて）の位置を、料金所写真の中で下方向に再配置。ナビゲーションバーとの間に余白を確保し、コンテンツが画像内にしっかりと収まる印象になりました。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.8.0",
    type: "Feature Release",
    date: "May 20, 2026 (JST)",
    sortDate: "2026-05-20T20:00:00+09:00",
    latest: false,
    title: {
      en: "Signboards across Japan now have their own home on the site",
      ja: "日本全国の看板掲出専用ページを新設",
    },
    desc: {
      en: "Today's release introduces a dedicated Signboards section that brings every NoJo Enterprise outdoor placement into one place — starting with three positions on Route 58 in Okinawa and the Yokosuka headquarters location. Each signboard has its own detail page with a full on-site photo gallery and quick navigation between locations.",
      ja: "本日のリリースでは、NoJo Enterpriseの屋外看板を一覧できる「看板掲出」専用ページを新設しました。沖縄・国道58号線の3区画と、横須賀本社拠点を含む全ての設置場所をまとめてご紹介します。各看板には専用の詳細ページがあり、現地写真ギャラリーと他の看板への素早いナビゲーションを備えています。",
    },
    changes: [],
    features: [
      {
        title: { en: "Centralized Signboards hub", ja: "看板の中央ハブページ" },
        desc:  { en: "A new top-level Signboards page groups every signboard by region — Okinawa today, Yokosuka home base, and the framework to add new regions as we expand across Japan. Each region has its own horizontally-scrolling preview gallery so visitors can flip through all the boards at a glance.", ja: "新設の「看板掲出」トップページでは、看板を地域別に整理してご紹介します。現在は沖縄エリアと横須賀本社拠点を掲載し、日本全国への展開に向けて新エリアを追加できる構造になっています。各エリアには横スクロールのプレビューギャラリーがあり、すべての看板を一目で確認できます。" },
      },
      {
        title: { en: "Per-signboard detail page with full on-site gallery", ja: "看板ごとの詳細ページと現地ギャラリー" },
        desc:  { en: "Each signboard now has a dedicated detail page showing every on-site photo from that location, along with the signboard's actual creative artwork, location, route, and status. A slot is reserved at the top of each page for a Google Street View link so visitors can confirm the board is up live.", ja: "看板ごとに専用の詳細ページを新設。各設置場所の現地写真、実際に掲出されているデザイン、所在地、路線、稼働状況を掲載しています。各ページの上部にはGoogle ストリートビューのリンク用スロットを確保しており、看板が実際に設置されている様子を確認いただけます。" },
      },
      {
        title: { en: "Sticky left-rail navigation between signboards", ja: "看板間を行き来できるサイドナビゲーション" },
        desc:  { en: "On every signboard detail page, a sticky left rail lists all the other signboards grouped by region — so visitors can hop straight from one signboard to another without going back to the hub page. On mobile, the same navigation appears as a horizontal pill scroller above the content.", ja: "看板詳細ページには、他の全看板を地域別にまとめたサイドナビゲーションを常時表示。ハブページに戻らずに、別の看板へ直接ジャンプできます。モバイルではコンテンツ上部に横スクロールのピル型ナビゲーションとして表示されます。" },
      },
      {
        title: { en: "Three Okinawa Route 58 positions plus Yokosuka home base live", ja: "沖縄・国道58号線の3区画と横須賀本社拠点を公開" },
        desc:  { en: "The launch includes three live signboard positions along Route 58 in Okinawa (Oyama, B-36A Camp Foster, and B-13) plus the Yokosuka headquarters location. Future expansion across Honshu, Kyushu, and Hokkaido is in the plan.", ja: "今回の公開では、沖縄・国道58号線の3区画（大山、B-36A キャンプ・フォスター、B-13）と、横須賀本社拠点を掲載しています。今後、本州、九州、北海道への展開も予定しています。" },
      },
    ],
    improvements: [
      {
        title: { en: "Signboards added to top navigation, mobile menu, and footer", ja: "ナビゲーション、モバイルメニュー、フッターに「看板掲出」を追加" },
        desc:  { en: "The new Signboards section is now reachable from the main top navigation, the mobile menu's pastel icon-card grid, the footer's Updates column, and the dedicated Sitemap page — so visitors can find it from any page on the site.", ja: "新設の「看板掲出」セクションは、メインナビゲーション、モバイルメニューのアイコンカード、フッターの「更新情報」列、サイトマップページからアクセスできます。サイト内のどのページからでも見つけられるように整理しています。" },
      },
      {
        title: { en: "Leadership Team navigation tabs now use brand colors", ja: "リーダーシップチームのナビゲーションタブをブランドカラーに変更" },
        desc:  { en: "The active-state pill on the People & Founder Story tab strip (Our AI Team · Leadership Team · Founder's Messages) now uses each section's brand color (blue for AI Team, NoJo purple for Leadership, JapanETCcard purple and JapanTollReceipts green for the founder messages) instead of a single dark navy chip.", ja: "「AIチーム・リーダーシップ・代表メッセージ」のタブ切り替えで、選択中のタブを各セクションのブランドカラー（AIチームはブルー、リーダーシップはNoJoパープル、JapanETCcardはJECパープル、JapanTollReceiptsはJTRグリーン）で表示するように変更。これまでの単色ネイビーから、ブランド体験に揃えた配色に統一しました。" },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.7.0",
    type: "Feature Release",
    date: "May 20, 2026 (JST)",
    sortDate: "2026-05-20T18:00:00+09:00",
    latest: false,
    title: {
      en: "Vibrant What's New, dedicated Technology + Trust pages, and a live release pulse",
      ja: "「最新情報」の刷新、テクノロジー・信頼ページの新設、リリースインジケーター",
    },
    desc: {
      en: "Today's release brings a more vibrant, JTR-aligned What's New page with per-service version chips, dedicated Technology and Trust & Security pages, and a small live indicator in the main navigation that pulses whenever any operated service ships a release.",
      ja: "本日のリリースでは、JTRのデザインに合わせた色鮮やかな「最新情報」ページ、専用の「テクノロジー」「信頼とセキュリティ」ページ、そして運営サービスがリリースを公開したときにナビゲーションでパルス点灯する小さなインジケーターを追加しました。",
    },
    changes: [],
    features: [
      {
        title: { en: "Per-service version chips in the What's New hero", ja: "「最新情報」ヘッダーにサービス別バージョン表示" },
        desc:  { en: "The What's New hero now shows the latest shipped version of every operated service (NoJo, JapanETCcard, JapanTollReceipts) side by side. Each chip is clickable and filters the page to that service.", ja: "「最新情報」のヘッダーに、運営する各サービス（NoJo・JapanETCcard・JapanTollReceipts）の最新公開バージョンを並べて表示。各チップをクリックすると、そのサービスで絞り込めます。" },
      },
      {
        title: { en: "Dedicated Technology page", ja: "テクノロジー専用ページを新設" },
        desc:  { en: "A new Technology page explains the three pillars (mobility · records · digital infrastructure) and the shared operating foundation that powers every NoJo Enterprise service.", ja: "「テクノロジー」専用ページを新設し、モビリティ・記録・デジタル基盤という3つの柱と、NoJo Enterpriseの全サービスを支える共通の運用基盤について解説しました。" },
      },
      {
        title: { en: "Dedicated Trust & Security page", ja: "信頼とセキュリティ専用ページを新設" },
        desc:  { en: "A new Trust & Security page documents the five operating principles and the published customer-data commitments — no commercial data sharing between services, audit-log discipline, minimal retention, transparent reporting.", ja: "「信頼とセキュリティ」専用ページを新設。5つの運用原則と、お客様データに関する公開された運用方針（サービス間での商用データ共有なし・監査ログの徹底・最小限のデータ保持・透明性のある報告）をご紹介しています。" },
      },
      {
        title: { en: "Live release pulse in the main navigation", ja: "ナビゲーションのライブリリースパルス" },
        desc:  { en: "A small dot now pulses next to \"What's New\" in the main navigation whenever any operated service has shipped a release in the last 48 hours, so visitors can spot fresh activity at a glance.", ja: "ナビゲーションの「最新情報」の横に、過去48時間以内にいずれかの運営サービスがリリースを公開した場合、小さなドットがパルス点灯。新しい動きを一目で確認できます。" },
      },
    ],
    improvements: [
      {
        title: { en: "Colorful, distinct filter palettes on What's New & Roadmap", ja: "「最新情報」「ロードマップ」のフィルターを色分けして整理" },
        desc:  { en: "Site filters use the brand colors (NoJo indigo · JEC purple · JTR green · JCM blue). Status filters now use a semantically distinct palette (purple/pink/blue/yellow/green/red), making it instantly clear whether you are filtering by site or by status.", ja: "「サイト」フィルターはブランドカラー（NoJoインディゴ・JECパープル・JTRグリーン・JCMブルー）、「ステータス」フィルターは意味のある別のカラー（パープル・ピンク・ブルー・イエロー・グリーン・レッド）で配色し、サイト絞り込みかステータス絞り込みかを一目で区別できるようにしました。" },
      },
      {
        title: { en: "More accurate \"Last verified\" labeling on freshness badges", ja: "鮮度バッジを「最終確認」表記に変更" },
        desc:  { en: "Freshness indicators now say \"Last verified N min ago\" instead of \"Updated N min ago\" — more accurate, since we are reporting when we last polled the upstream source, not necessarily when the upstream changed.", ja: "鮮度インジケーターを「最終確認 ◯分前」表記に変更しました。上流データソースを最後に確認した時刻を示す表現で、上流の更新時刻とは限らないため、より正確な表現に変更しています。" },
      },
      {
        title: { en: "Condensed, more readable What's New hero", ja: "「最新情報」ヘッダーをコンパクトに調整" },
        desc:  { en: "The What's New hero band is now more compact, with the latest-update date and the per-service version chips fitting cleanly above the fold on both desktop and mobile.", ja: "「最新情報」のヘッダーをコンパクトに調整。デスクトップ・モバイルともに、最終更新日とサービス別バージョンチップを上半分にすっきり収めました。" },
      },
      {
        title: { en: "Homepage CTAs use the brand-logo purple", ja: "ホームページの主要ボタンにブランドロゴのパープルを反映" },
        desc:  { en: "The new homepage \"See What's New\" call-to-action uses the official logo purple, so the corporate hero now reads as purple / blue / green — exactly the brand's three colors.", ja: "新設のホームページ「最新情報を見る」ボタンに、公式ロゴと同じパープルを採用。コーポレートヘッダーがパープル・ブルー・グリーンというブランドの3色構成で揃いました。" },
      },
    ],
    fixes: [
      {
        title: { en: "Roadmap status-filter pills no longer clip at the bottom edge", ja: "ロードマップのステータスフィルターの見切れを解消" },
        desc:  { en: "The Roadmap filter rows now have proper vertical breathing room — status chips like Launch and Completed are no longer clipped at the bottom edge.", ja: "ロードマップのフィルター行に十分な縦方向の余白を追加。ローンチ・完了などのステータスチップが下端で見切れる問題を解消しました。" },
      },
    ],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.6.0",
    type: "Feature Release",
    date: "May 20, 2026 (JST)",
    sortDate: "2026-05-20T15:00:00+09:00",
    latest: false,
    title: {
      en: "More polished What's New & Roadmap, tooltip refresh, and JCM roadmap update",
      ja: "「最新情報」「ロードマップ」の見栄え向上、ツールチップ刷新、JCMロードマップ更新",
    },
    desc: {
      en: "Several visual and structural improvements landed today. Each release on the consolidated What's New now renders with the same colored category breakdown the operated services show on their own pages, and the official-letter tooltip on the homepage was refreshed.",
      ja: "本日、視覚面・構造面の改善を複数リリースしました。統合された「最新情報」ページで、各リリースに「改善・新機能・修正・セキュリティ」のカテゴリー別表示を適用。ホームページの公式書簡ボタンのツールチップも刷新しました。",
    },
    changes: [],
    features: [
      {
        title: {
          en: "Per-category change blocks on every release",
          ja: "各リリースにカテゴリー別の変更ブロックを表示",
        },
        desc: {
          en: "Releases on the consolidated What's New now display New Feature, Improvement, Bug Fix, Security, and Note items with the same visual treatment used on the operated-service changelog pages.",
          ja: "統合された「最新情報」のリリース表示に、新機能・改善・バグ修正・セキュリティ・ノートのカテゴリーを、運営サービスのチェンジログページと同じビジュアルで表示するようにしました。",
        },
      },
    ],
    improvements: [
      {
        title: {
          en: "Polished hover tooltip on the official-letter button",
          ja: "公式書簡ボタンのホバーツールチップを刷新",
        },
        desc: {
          en: "The hover tooltip on the NBC Olympics official-letter trust badge was redesigned to feel more brand-aligned and to avoid clipping at the edge of containing cards.",
          ja: "NBCオリンピックス公式書簡バッジのホバーツールチップを、ブランドに沿ったデザインに刷新。カードの端で見切れる問題も解消しました。",
        },
      },
      {
        title: {
          en: "JapanChangeMoney roadmap entries refreshed",
          ja: "JapanChangeMoneyのロードマップを刷新",
        },
        desc: {
          en: "The public roadmap now shows the JapanChangeMoney complete rebuild and the 2027 public-launch target, replacing earlier 2026 references across the site.",
          ja: "公開ロードマップに、JapanChangeMoneyの全面リニューアルおよび2027年公開予定を反映。サイト上の旧2026年表記もあわせて更新しました。",
        },
      },
    ],
    fixes: [],
    security: [],
    notes: [],
    tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.5.0",
    type: "Feature Release",
    date: "May 20, 2026 (JST)",
    sortDate: "2026-05-20T13:00:00+09:00",
    latest: false,
    title: {
      en: "Brand colors across every call-to-action",
      ja: "主要ボタンにブランドカラーを反映",
    },
    desc: {
      en: "Primary buttons across the corporate site now use NoJo's blue, green, and purple consistently. The experience feels more colorful and on-brand, especially on mobile where the menu now leads with brand-color accents.",
      ja: "コーポレートサイト全体の主要ボタンを、NoJoのブルー、グリーン、パープルで統一しました。モバイルメニューでもブランドカラーのアクセントが目立つようになり、より色彩豊かで一貫した印象になっています。",
    },
    changes: [],
    features: [],
    improvements: [
      {
        title: { en: "Brand-color CTAs replace neutral black buttons",
                 ja: "黒い主要ボタンをブランドカラーに刷新" },
        desc:  { en: "Primary call-to-action buttons across the corporate site now use NoJo's blue, green, and purple consistently, giving the experience a more colorful, on-brand feel.",
                 ja: "コーポレートサイト全体の主要ボタンを、NoJoのブルー・グリーン・パープルで統一。より色彩豊かでブランドらしい印象になっています。" },
      },
      {
        title: { en: "Mobile menu now leads with color accents",
                 ja: "モバイルメニューにブランドカラーのアクセントを追加" },
        desc:  { en: "Mobile menu Quick Links each carry a left-edge brand-color stripe — purple for About, blue for Services + Roadmap, green for What's New — so the menu reads at a glance.",
                 ja: "モバイルメニューの「クイックリンク」に左端のブランドカラー帯を追加。Aboutはパープル、Services・Roadmapはブルー、What's Newはグリーンと色分けし、一目で識別できるようにしました。" },
      },
    ],
    fixes: [], security: [], notes: [], tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.4.0",
    type: "Feature Release",
    date: "May 20, 2026 (JST)",
    sortDate: "2026-05-20T12:00:00+09:00",
    latest: false,
    title: {
      en: "NoJo Enterprise joins What's New and Roadmap",
      ja: "NoJo Enterpriseが最新情報・ロードマップに統合",
    },
    desc: {
      en: "Corporate updates from NoJo Enterprise now appear together with JapanETCcard and JapanTollReceipts on the same What's New and Roadmap pages. A service filter lets visitors view only NoJo Enterprise, one operated service, or all of them at once.",
      ja: "NoJo Enterpriseのコーポレート更新を、JapanETCcardやJapanTollReceiptsと同じ「最新情報」「ロードマップ」ページに統合しました。サービスフィルターで、NoJo Enterprise単体、各運営サービス単体、またはすべてをまとめて表示できます。",
    },
    changes: [],
    features: [
      {
        title: { en: "NoJo Enterprise as a third service filter",
                 ja: "サービスフィルターに「NoJo Enterprise」を追加" },
        desc:  { en: "What's New and Roadmap pages now expose a NoJo Enterprise filter chip alongside JapanETCcard and JapanTollReceipts, so visitors can drill into just the corporate updates if they prefer.",
                 ja: "「最新情報」「ロードマップ」ページにNoJo Enterpriseのフィルターチップを追加。JapanETCcardやJapanTollReceiptsと並べて、コーポレートの更新のみを絞り込んで表示できます。" },
      },
    ],
    improvements: [
      {
        title: { en: "Single consolidated view across the ecosystem",
                 ja: "エコシステム全体を一覧できる統合ビュー" },
        desc:  { en: "Instead of checking each service's changelog separately, the consolidated view merges all three sources into one timeline ordered newest-first.",
                 ja: "各サービスのチェンジログを個別に確認する必要がなくなり、3つの情報源を「新しい順」で統合表示するタイムラインで一覧できるようになりました。" },
      },
    ],
    fixes: [], security: [], notes: [], tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.2.0",
    type: "Feature Release",
    date: "May 20, 2026 (JST)",
    sortDate: "2026-05-20T10:30:00+09:00",
    latest: false,
    title: {
      en: "Cleaner mobile navigation",
      ja: "モバイルナビゲーションをすっきり整理",
    },
    desc: {
      en: "On mobile, leadership, team, founder messages, and careers are now grouped together under Company — the result is a shorter, easier-to-scan menu on smaller screens.",
      ja: "モバイル表示で、経営、チーム、代表メッセージ、採用情報を「コーポレート」配下にまとめました。小さな画面でもメニューが短く、目的のページにたどり着きやすくなっています。",
    },
    changes: [], features: [],
    improvements: [
      {
        title: { en: "Leadership, team, founder messages, and careers grouped under Company",
                 ja: "経営・チーム・代表メッセージ・採用情報を「コーポレート」配下に統合" },
        desc:  { en: "Mobile menu was restructured so related pages live together under a single Company section instead of being scattered as flat top-level entries.",
                 ja: "モバイルメニューを再構成し、関連するページをトップレベルに散らばらせず「コーポレート」セクション配下にまとめました。" },
      },
      {
        title: { en: "Shorter, easier-to-scan mobile menu",
                 ja: "モバイルメニューを短く、見やすく" },
        desc:  { en: "Top-level menu items were reduced from 11 to 7, with deeper navigation reachable in one tap through expandable sections.",
                 ja: "トップレベルのメニュー項目を11個から7個に削減。詳細ページにも、開閉式セクション経由でワンタップで到達できるようにしました。" },
      },
    ],
    fixes: [], security: [], notes: [], tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.1.0",
    type: "Patch",
    date: "May 19, 2026 (JST)",
    sortDate: "2026-05-19T18:00:00+09:00",
    latest: false,
    title: {
      en: "Sharing a page now shows the correct preview",
      ja: "ページ共有時のプレビューを正しく表示",
    },
    desc: {
      en: "Resolved an issue where sharing certain pages on social networks could show the generic homepage card instead of the page being shared. Every shared link now previews the correct title, summary, and image.",
      ja: "一部のページをSNSで共有した際、ページ固有のプレビューではなくホームページのプレビューが表示される事象を解消しました。共有リンクには、共有元のページに合ったタイトル、要約、画像が正しく表示されます。",
    },
    changes: [], features: [], improvements: [],
    fixes: [
      {
        title: { en: "Page-specific share previews now load correctly",
                 ja: "ページごとの共有プレビューを正しく表示" },
        desc:  { en: "Some pages shared on social networks were showing the generic homepage card instead of the page being shared. Every shared link now previews the correct title, summary, and image for the actual page.",
                 ja: "SNSで一部のページを共有した際、ページ固有のプレビューではなくホームページのプレビューが表示されていた問題を解消。共有リンクには、共有元ページの正しいタイトル・要約・画像が表示されます。" },
      },
    ],
    security: [], notes: [], tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
  {
    version: "v3.0.0",
    type: "Feature Release",
    date: "May 18, 2026 (JST)",
    sortDate: "2026-05-18T17:00:00+09:00",
    latest: false,
    title: {
      en: "Corporate Profile refreshed with verified company information",
      ja: "会社概要を最新の公式情報で更新",
    },
    desc: {
      en: "The Corporate Profile page was refreshed with verified, up-to-date company information so partners, customers, and prospective hires can rely on a single, accurate source.",
      ja: "会社概要ページを、最新の公式情報で更新しました。お取引先・お客様・採用候補の方々に、信頼できる単一の情報源としてご利用いただけます。",
    },
    changes: [], features: [],
    improvements: [
      {
        title: { en: "Verified company information now drives Corporate Profile",
                 ja: "会社概要を確認済みの公式情報で更新" },
        desc:  { en: "The Corporate Profile page is now populated from verified, up-to-date official information — capital, registered scope of business, contact details — so partners and customers can rely on a single accurate source.",
                 ja: "会社概要ページを、最新の公式情報（資本金、定款記載の事業目的、連絡先など）で構成。お取引先やお客様が安心して参照できる「正確な単一情報源」として運用します。" },
      },
    ],
    fixes: [], security: [], notes: [], tags: [],
    _serviceId: SERVICE_ID,
    _serviceColorHex: SERVICE_HEX,
  },
];

// Quarters NoJo items contribute, so the roadmap page renders a
// quarter swim-lane even when JEC/JTR have no items in that bucket.
export const NOJO_ROADMAP_QUARTERS = ["q2_2026", "q3_2026", "q4_2026", "q1_2027"];
